import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Sparkles, ArrowRight, Zap } from "lucide-react";
import { Link } from "react-router-dom";
import { PlanTier, PLAN_LIMITS } from "@/lib/plan-limits";
import { cn } from "@/lib/utils";

interface UpgradePromptProps {
  currentPlan: PlanTier;
  feature?: string;
  reason?: string;
  compact?: boolean;
  className?: string;
}

const UPGRADE_PATH: Record<PlanTier, PlanTier | null> = {
  free: 'launch',
  launch: 'growth',
  growth: 'opspro',
  opspro: null,
};

export function UpgradePrompt({
  currentPlan,
  feature,
  reason,
  compact = false,
  className,
}: UpgradePromptProps) {
  const nextPlan = UPGRADE_PATH[currentPlan];
  
  if (!nextPlan) {
    return null; // Already on highest plan
  }

  const nextPlanLimits = PLAN_LIMITS[nextPlan];

  if (compact) {
    return (
      <div className={cn("flex items-center gap-3 p-3 rounded-lg bg-primary/5 border border-primary/20", className)}>
        <Sparkles className="h-4 w-4 text-primary flex-shrink-0" />
        <p className="text-sm text-muted-foreground flex-1">
          {reason || `Upgrade to ${nextPlan.charAt(0).toUpperCase() + nextPlan.slice(1)} to unlock ${feature || 'more features'}.`}
        </p>
        <Link to="/pricing">
          <Button size="sm" variant="hero" className="gap-1">
            Upgrade <ArrowRight className="h-3 w-3" />
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <Card className={cn("relative overflow-hidden", className)}>
      <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent" />
      <CardHeader className="relative">
        <div className="flex items-center gap-2 mb-2">
          <Badge variant="secondary" className="bg-primary/10 text-primary">
            <Zap className="h-3 w-3 mr-1" />
            {nextPlan.charAt(0).toUpperCase() + nextPlan.slice(1)} Plan
          </Badge>
        </div>
        <CardTitle className="text-lg">Unlock More Power</CardTitle>
        <CardDescription>
          {reason || `Get ${feature ? `${feature} and ` : ''}more with ${nextPlan.charAt(0).toUpperCase() + nextPlan.slice(1)}.`}
        </CardDescription>
      </CardHeader>
      <CardContent className="relative space-y-4">
        <ul className="space-y-2 text-sm">
          <li className="flex items-center gap-2">
            <Sparkles className="h-3.5 w-3.5 text-primary" />
            Up to {nextPlanLimits.maxSeats} team members
          </li>
          <li className="flex items-center gap-2">
            <Sparkles className="h-3.5 w-3.5 text-primary" />
            {nextPlanLimits.rateLimits.aiCallsPerDay} AI calls/day
          </li>
          {nextPlanLimits.features.advancedAnalytics && (
            <li className="flex items-center gap-2">
              <Sparkles className="h-3.5 w-3.5 text-primary" />
              Advanced analytics
            </li>
          )}
          {nextPlanLimits.features.apiAccess && (
            <li className="flex items-center gap-2">
              <Sparkles className="h-3.5 w-3.5 text-primary" />
              API access
            </li>
          )}
        </ul>
        
        <div className="flex items-center justify-between pt-2">
          <div>
            <span className="text-2xl font-bold">₹{nextPlanLimits.priceINR}</span>
            <span className="text-muted-foreground text-sm">/month</span>
          </div>
          <Link to="/pricing">
            <Button variant="hero" className="gap-2">
              Upgrade Now <ArrowRight className="h-4 w-4" />
            </Button>
          </Link>
        </div>
      </CardContent>
    </Card>
  );
}
